import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type DisplayPreviewSlide = {
  id: string;
  /** Draft playlist_items.id — present when the slide can be removed from a direct show. */
  itemId?: string;
  src: string;
  kind: 'image' | 'video';
  durationSeconds: number;
};

type DisplayPreviewCarouselProps = {
  slides: DisplayPreviewSlide[];
  fallbackLabel: string;
};

/**
 * Auto-advancing preview of the screen's assigned playlist media.
 */
export function DisplayPreviewCarousel({ slides, fallbackLabel }: DisplayPreviewCarouselProps) {
  const { t } = useTranslation('displays');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const current = slides[index];
    if (!current) return;
    const ms = Math.max(2, current.durationSeconds) * 1000;
    const timer = window.setTimeout(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, ms);
    return () => window.clearTimeout(timer);
  }, [slides, index]);

  if (slides.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 px-4 text-center">
        <span className="text-[13px] font-bold text-signal">{t('preview.noPlaylistPreview')}</span>
        <span className="text-[11.5px] text-muted">{fallbackLabel}</span>
        <span className="text-[11px] text-muted">{t('preview.publishHint')}</span>
      </div>
    );
  }

  const slide = slides[index] ?? slides[0]!;

  return (
    <div className="relative h-full w-full bg-black">
      {slide.kind === 'video' ? (
        <video
          key={slide.id}
          src={slide.src}
          className="h-full w-full object-cover"
          muted
          autoPlay
          loop
          playsInline
          aria-hidden
        />
      ) : (
        <img
          key={slide.id}
          src={slide.src}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-2.5 pb-2 pt-6">
        <p className="m-0 truncate text-[11px] font-medium text-white/90">{fallbackLabel}</p>
        {slides.length > 1 ? (
          <div className="mt-1.5 flex items-center justify-center gap-1">
            {slides.map((item, i) => (
              <span
                key={item.id}
                className={
                  i === index
                    ? 'h-1.5 w-1.5 rounded-full bg-white'
                    : 'h-1.5 w-1.5 rounded-full bg-white/40'
                }
                aria-hidden
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
